/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { usePlaidEventTracking } from './use-plaid-event-tracking';
import { usePlaidStyleOverride } from './use-plaid-style-override';
import { initiateRequestForPayment } from '../../../services';

export const usePlaid = ({
  paymentAmount,
  onSuccess,
  onExit,
  onError
}: {
  paymentAmount: number;
  onSuccess: () => void;
  onExit: () => void;
  onError: () => void;
}) => {
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);
  const [hidePlaid, setHidePlaid] = useState(true);

  const plaidStyleOverride = usePlaidStyleOverride(hidePlaid);
  const { trackPlaidEvent } = usePlaidEventTracking();

  const { open, ready } = usePlaidLink({
    clientName: 'Carvana',
    env: `${process.env.PLAID_ENV}`,
    token: plaidLinkToken,
    onSuccess: () => {
      trackPlaidEvent('SUCCESS');
      onSuccess();
    },
    onEvent: (eventName, metadata) => {
      if (hidePlaid) {
        setHidePlaid(false);
      }

      if (eventName !== 'TRANSITION_VIEW') {
        trackPlaidEvent(eventName, metadata);
      }
    },
    onExit: (error, metadata) => {
      if (error) {
        trackPlaidEvent(`ERROR_${error.display_message}`, metadata);
        onError();
        return;
      }

      trackPlaidEvent('EXIT', metadata);
      onExit();
    }
  });

  // Guard with no cleanup, so StrictMode's second mount run skips instead of creating a second
  // payment request server-side, whose token would make react-plaid-link destroy the open handler.
  const hasInitiatedPaymentRef = useRef(false);
  useEffect(() => {
    if (hasInitiatedPaymentRef.current) return;
    hasInitiatedPaymentRef.current = true;
    initiateRequestForPayment()
      .then((response: { linkToken: string; amount: number }) => {
        if (response.amount.toFixed(2) !== paymentAmount.toFixed(2)) {
          throw new Error('Payment amount mismatch');
        }
        setPlaidLinkToken(response.linkToken);
      })
      .catch(() => {
        onError();
      });
  }, []);

  useEffect(() => {
    if (ready) {
      trackPlaidEvent('START');
      open();

      const timeout = setTimeout(() => {
        setHidePlaid(false);
      }, 500);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [ready]);

  return { plaidStyleOverride };
};
